"use client";

import { useEffect, useSyncExternalStore } from "react";
import { bff } from "@/lib/api";
import { isIngDeal } from "@/lib/ing-deals";
import { useSiteContext } from "@/components/providers/SiteContextProvider";
import {
  FETCH_TIMEOUT_MS,
  FILM_SITE,
  FILM_UIT_KEY,
  FILM_HOST,
  POSTER_HOSTS,
  eigenUrl,
  getSessieStorage,
  getStorage,
  magFilmInBrowser,
  naLoadEnIdle,
  proefGroep,
  pushData,
} from "@/lib/film-voorwaarden";

// Dealvideo's uit het backendregister (GET /deal-video, vlag DEAL_VIDEO_PUBLIEK;
// uit = 404). Eén lezing per pagina, na `load` + idle, met een time-out van
// 1500 ms. Uitkomst: een map `deals_id -> { kaart, hero, band, poster, duur_s }`.
// 404, een fout of een time-out geeft een lege map en dan blijven de foto's
// staan. Dezelfde voorwaarden als HeroFilm (@/lib/film-voorwaarden).

export const DEALFILM_PROEF_KEY = "ft_dealfilm_proef";
const LEEG = Object.freeze({});
const SERVER_STAND = Object.freeze({ map: LEEG, status: "idle", groep: null });

/**
 * Geldige filmmap uit de backendbody. Elke URL moet https op library.favotrip.nl
 * zijn; een vreemde host valt weg (de andere velden van die deal blijven staan,
 * een deal zonder bruikbare film verdwijnt). De body mag een lijst zijn, of een
 * object met `videos`/`deals`/`items` als lijst, of een map op deals_id.
 */
export function geldigeDealFilms(body) {
  const uit = {};
  if (!body || typeof body !== "object") return uit;
  let rijen;
  if (Array.isArray(body)) rijen = body.map((r) => [r && r.deals_id, r]);
  else {
    const lijst = ["videos", "deals", "items"].map((k) => body[k]).find((v) => Array.isArray(v));
    if (lijst) rijen = lijst.map((r) => [r && r.deals_id, r]);
    else rijen = Object.entries(body).map(([k, r]) => [r && typeof r === "object" && r.deals_id != null ? r.deals_id : k, r]);
  }
  for (const [id, r] of rijen) {
    if (id == null || id === "" || !r || typeof r !== "object") continue;
    const url = (v) => eigenUrl(v && typeof v === "object" ? v.url : v, [FILM_HOST]);
    const kaart = url(r.kaart);
    const hero = url(r.hero);
    const band = url(r.band);
    if (!kaart && !hero && !band) continue;
    uit[String(id).trim()] = {
      kaart,
      hero,
      band,
      poster: eigenUrl(r.poster, POSTER_HOSTS),
      duur_s: typeof r.duur_s === "number" && Number.isFinite(r.duur_s) ? r.duur_s : null,
    };
  }
  return uit;
}

// --- Eén gedeelde lezing per pagina ----------------------------------------

let stand = SERVER_STAND;
let gestart = false;
let generatie = 0;
let stopLaden = null;
let laatsteDomain = null;
const luisteraars = new Set();

function zet(nieuw) {
  stand = nieuw;
  luisteraars.forEach((l) => l());
}

// Eén vaste storage-listener, aangemeld bij de eerste abonnee en opgeruimd bij het
// afmelden van de laatste. "Beweging uit" in een ander tabblad zet FILM_UIT_KEY;
// dat komt hier binnen als `storage`-event (key null = hele opslag gewist).
function opStorage(e) {
  if (!e || (e.key !== FILM_UIT_KEY && e.key !== null)) return;
  if (e.key === null && !bewegingIsUit()) return;
  stopFilms();
}

// "Minder beweging" tijdens het kijken aangezet: stop de films; teruggezet: alleen seinen.
function opReducedMotion(e) {
  if (e && e.matches) stopFilms({ leeg: true });
  else luisteraars.forEach((l) => l());
}

let mql = null;

function meldMql() {
  try {
    mql = window.matchMedia ? window.matchMedia("(prefers-reduced-motion: reduce)") : null;
    if (!mql) return;
    if (mql.addEventListener) mql.addEventListener("change", opReducedMotion);
    else if (mql.addListener) mql.addListener(opReducedMotion);
  } catch {
    mql = null;
  }
}

function meldMqlAf() {
  const m = mql;
  mql = null;
  if (!m) return;
  try {
    if (m.removeEventListener) m.removeEventListener("change", opReducedMotion);
    else if (m.removeListener) m.removeListener(opReducedMotion);
  } catch {
    // niets op te ruimen
  }
}

function abonneer(cb) {
  if (luisteraars.size === 0 && typeof window !== "undefined") {
    try {
      window.addEventListener("storage", opStorage);
    } catch {
      // geen window-events: dan alleen dit tabblad
    }
    meldMql();
  }
  luisteraars.add(cb);
  return () => {
    luisteraars.delete(cb);
    if (luisteraars.size === 0 && typeof window !== "undefined") {
      try {
        window.removeEventListener("storage", opStorage);
      } catch {
        // niets op te ruimen
      }
      meldMqlAf();
    }
  };
}

// Stopt de kaartfilms en seint alle abonnees. De registergegevens blijven geladen,
// zodat een nieuw gemonteerde dealhero zijn play-knop kan tonen. Alleen met `leeg`
// (reduced-motion) gaat de gedeelde map weg; `gestart` gaat dan in dezelfde stap
// terug, zodat de volgende montage of play het register opnieuw leest.
function stopFilms({ leeg = false } = {}) {
  if (!leeg) {
    luisteraars.forEach((l) => l());
    return;
  }
  generatie += 1;
  if (stopLaden) stopLaden();
  stopLaden = null;
  gestart = false;
  zet({ ...stand, map: LEEG });
}

/**
 * Pauze ("Beweging uit"): zet de uit-sleutel en seint alle abonnees, zodat spelende
 * en nieuw verschijnende kaartfilms meteen de foto tonen. De registergegevens
 * blijven geladen.
 */
export function zetBewegingUit({ sessie = false } = {}) {
  try {
    const s = sessie ? getSessieStorage() : getStorage();
    if (s) s.setItem(FILM_UIT_KEY, "1");
  } catch {
    // opslaan lukt niet: geldt alleen voor deze pagina
  }
  stopFilms();
}

/**
 * Pauzeknop op "play": haalt de uit-sleutel weg, seint alle abonnees en start de
 * registerlezing opnieuw als die door de voorkeur of een eerdere stop was overgeslagen.
 */
export function zetBewegingAan() {
  try {
    // "Beweging aan" geldt op elke film: de uit-voorkeur gaat uit BEIDE opslagen
    // (een homepage-pauze staat in sessionStorage, een dealpagina-pauze in localStorage).
    const t = getSessieStorage();
    if (t) t.removeItem(FILM_UIT_KEY);
    const s = getStorage();
    if (s) s.removeItem(FILM_UIT_KEY);
  } catch {
    // verwijderen lukt niet: geldt alleen voor deze pagina
  }
  if (!gestart && stand.map === LEEG) {
    startDealFilms({ domain: laatsteDomain });
  }
  luisteraars.forEach((l) => l());
}

/**
 * Deals die nooit een film krijgen, via welke kaart of pagina ook: ING en andere
 * extern verkochte deals (dezelfde herkenning als IngDealCard en de dealpagina).
 */
export function dealFilmUitgesloten(deal, { locale = "nl" } = {}) {
  return isIngDeal(deal, { locale });
}

/** Staat "Beweging uit" aan in deze browser? */
export function bewegingIsUit() {
  try {
    const s = getStorage();
    const t = getSessieStorage();
    return (!!s && s.getItem(FILM_UIT_KEY) === "1") || (!!t && t.getItem(FILM_UIT_KEY) === "1");
  } catch {
    return true;
  }
}

/** Abonneer op wijzigingen van de filmstand (voor useSyncExternalStore). */
export function abonneerFilmStand(cb) {
  return abonneer(cb);
}

/** Alleen voor tests: terug naar de beginstand. */
export function resetDealFilms() {
  if (stopLaden) stopLaden();
  stopLaden = null;
  gestart = false;
  laatsteDomain = null;
  generatie += 1;
  stand = SERVER_STAND;
}

async function lees() {
  const afbreek = new AbortController();
  const timer = setTimeout(() => afbreek.abort(), FETCH_TIMEOUT_MS);
  try {
    const base = String(bff.defaults.baseURL || "").replace(/\/+$/, "");
    const res = await fetch(`${base}/deal-video`, { credentials: "omit", signal: afbreek.signal });
    if (res && res.status === 404) return { map: LEEG, status: "404" };
    if (!res || res.status !== 200) throw new Error(`status ${res && res.status}`);
    return { map: geldigeDealFilms(await res.json()), status: "register" };
  } catch (err) {
    console.debug("dealfilm: geen register", err && err.message);
    return { map: LEEG, status: "fout" };
  } finally {
    clearTimeout(timer);
  }
}

/** Start de ene lezing (idempotent) als de voorwaarden het toestaan. */
export function startDealFilms({ domain, random = Math.random, ookUit = false } = {}) {
  if (gestart || typeof window === "undefined") return;
  if (domain) laatsteDomain = domain;
  if (domain !== FILM_SITE) return;
  const storage = getStorage();
  const groep = storage ? proefGroep(storage, random, DEALFILM_PROEF_KEY) : "foto";
  if (groep !== "film") return;
  // Met de voorkeur gezet leest alleen een aanroep met `ookUit` (de dealhero, voor zijn
  // play-knop) het register; pagina's met alleen kaarten vragen het niet op.
  if (!ookUit && bewegingIsUit()) return;
  if (!magFilmInBrowser({ domain, storage, uitKey: null })) return;
  gestart = true;
  const mijn = generatie;
  stopLaden = naLoadEnIdle(async () => {
    const uitkomst = await lees();
    if (mijn === generatie) zet({ ...uitkomst, groep });
  });
}

/**
 * Stand van het register: { map, status, groep }. status: "idle" (niet
 * gelezen), "register" (200), "404" of "fout". Server en eerste client-render
 * zijn altijd de lege stand, zodat de server-HTML ongewijzigd blijft.
 * `actief: false` leest alleen mee en start de lezing niet. `ookUit: true` leest het
 * register ook met de voorkeur "Beweging uit" gezet (voor de play-knop van de dealhero).
 */
export function useDealFilms({ actief = true, random, ookUit = false } = {}) {
  const { domain } = useSiteContext();
  const nu = useSyncExternalStore(abonneer, () => stand, () => SERVER_STAND);
  useEffect(() => {
    if (actief) startDealFilms({ domain, random, ookUit });
  }, [actief, domain, ookUit]); // eslint-disable-line react-hooks/exhaustive-deps
  return nu;
}

/** Film van een deal uit de stand, of null. */
export function dealFilmUit(stand_, dealsId) {
  if (dealsId == null || dealsId === "") return null;
  const key = String(dealsId).trim();
  return Object.prototype.hasOwnProperty.call(stand_.map, key) ? stand_.map[key] : null;
}

export function meldDealFilm(soort, { dealsId, plek, groep }) {
  // "pauze" en "afspelen" (pauzeknop) gaan via dezelfde bestaande eventnaam, met `soort`.
  const bediening = soort === "pauze" || soort === "afspelen" ? { soort } : {};
  pushData({
    event: soort === "fout" ? "dealfilm_fout" : "dealfilm_getoond",
    deals_id: dealsId,
    plek,
    proefgroep: groep,
    ...bediening,
  });
}
